import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Value } from 'slate';
import { EmojiIcon } from '../../components/Icons';
import EntityTypes from '../../types/EntityTypes';
import createStore from '../utils/createStore';
import EmojiPicker from './components/Picker';
import isHotKey from 'is-hotkey';
import { defaultState, open, close, select, search, commit, arrowKeyHandler, commitKeyHandler, moveUp, moveDown, moveLeft, moveRight } from './state';
import makeToolbarButton, { EXPANDED_INSERTION } from '../utils/makeToolbarButton';

function createEmojiPickerPlugin() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$bottomSpace = _ref.bottomSpace,
      bottomSpace = _ref$bottomSpace === void 0 ? 0 : _ref$bottomSpace;

  var store = createStore({
    state: defaultState
  });

  function updatePluginState(modifierFn) {
    var payload = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    store.updateItem('state', modifierFn(store.getItem('state'), payload));
  }

  var throttleInputTimeout;

  var throttleInput = function throttleInput(fn) {
    clearTimeout(throttleInputTimeout);
    throttleInputTimeout = setTimeout(fn, 5);
  };

  function renderEditor(props, editor, next) {
    var children = next();
    return React.createElement(Fragment, null, children, !props.readOnly && React.createElement(EmojiPicker, {
      pluginStore: store,
      bottomSpace: bottomSpace,
      editor: editor,
      onSelect: function onSelect(emoji, index) {
        updatePluginState(select, {
          emoji: emoji,
          index: index
        });
      },
      onClick: function onClick(emoji, index) {
        updatePluginState(commit, {
          emoji: emoji,
          index: index,
          editor: editor
        });
      }
    }));
  }

  renderEditor.propTypes = {
    value: PropTypes.instanceOf(Value).isRequired,
    readOnly: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired
  };
  return [{
    serializers: {
      html: _defineProperty({}, EntityTypes.EMOJI, function (_ref2) {
        var node = _ref2.node,
            attributes = _ref2.attributes,
            children = _ref2.children;
        return React.createElement("span", attributes, node.data.get('emojiId'));
      })
    },
    onChange: function onChange(editor, next) {
      if (store.getItem('state').isVisible) {
        throttleInput(function () {
          updatePluginState(search, {
            value: editor.value
          });
        });
      }

      return next();
    },
    onKeyDown: function onKeyDown(event, editor, next) {
      if (event.key === ':' && editor.value.selection.isSet) {
        return throttleInput(function () {
          updatePluginState(open, {
            value: editor.value
          });
        });
      }

      if (!store.getItem('state').isVisible) {
        return next();
      }

      if (isHotKey('up', event)) {
        return arrowKeyHandler(event, editor.value, store, moveUp);
      }

      if (isHotKey('down', event)) {
        return arrowKeyHandler(event, editor.value, store, moveDown, {
          keyForFirstInteraction: true
        });
      }

      if (isHotKey('left', event)) {
        return arrowKeyHandler(event, editor.value, store, moveLeft);
      }

      if (isHotKey('right', event)) {
        return arrowKeyHandler(event, editor.value, store, moveRight, {
          keyForFirstInteraction: true
        });
      }

      if (isHotKey('tab', event)) {
        return commitKeyHandler(event, editor, store);
      }

      if (isHotKey('enter', event)) {
        if (store.getItem('state').index >= 0) {
          return commitKeyHandler(event, editor, store);
        }

        throttleInput(function () {
          updatePluginState(close);
        });
      }

      if (isHotKey('escape', event)) {
        event.stopPropagation();
        event.preventDefault();
        throttleInput(function () {
          updatePluginState(close);
        });
        return true;
      }

      return next();
    },
    renderEditor: renderEditor
  }, makeToolbarButton(EntityTypes.EMOJI, {
    icon: EmojiIcon,
    title: 'Emoji',
    visibility: EXPANDED_INSERTION,
    onApply: function onApply(editor) {
      editor.insertText(':');
      updatePluginState(open, {
        value: editor.value
      });
    }
  })];
}

export default createEmojiPickerPlugin;